import React, { useState, useEffect, useReducer, useMemo } from "react";
import { useParams } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import Paper from "@material-ui/core/Paper";

import TicketListItem from "../TicketListItemCustom";
import TicketsListSkeleton from "../TicketsListSkeleton";

import useTickets from "../../hooks/useTickets";
import { i18n } from "../../translate/i18n";
import { socketConnection } from "../../services/socket";

const useStyles = makeStyles((theme) => ({
    ticketsListWrapper: {
        position: "relative",
        display: "flex",
        height: "100%",
        flexDirection: "column",
        overflow: "hidden",
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
    },

    ticketsList: {
        flex: 1,
        maxHeight: "100%",
        overflowY: "scroll",
        ...theme.scrollbarStyles,
        borderTop: theme.palette.type === "dark"
            ? "1px solid rgba(255,255,255,0.06)"
            : "1px solid #E2E8F0",
        padding: "4px 6px",
    },

    ticketsListHeader: {
        color: theme.palette.type === "dark" ? "#94A3B8" : "#64748B",
        zIndex: 2,
        backgroundColor: theme.palette.background.paper,
        borderBottom: theme.palette.type === "dark"
            ? "1px solid rgba(255,255,255,0.06)"
            : "1px solid #E2E8F0",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },

    ticketsCount: {
        fontFamily: '"Inter", "Poppins", sans-serif',
        fontWeight: 500,
        color: theme.palette.type === "dark" ? "#94A3B8" : "#64748B",
        marginLeft: "8px",
        fontSize: "13px",
    },

    noTicketsText: {
        fontFamily: '"Inter", "Poppins", sans-serif',
        textAlign: "center",
        color: theme.palette.type === "dark" ? "#94A3B8" : "#64748B",
        fontSize: "14px",
        lineHeight: "1.4",
    },

    noTicketsTitle: {
        fontFamily: '"Inter", "Poppins", sans-serif',
        textAlign: "center",
        fontSize: "16px",
        fontWeight: 600,
        margin: "0px",
        color: theme.palette.type === "dark" ? "#F1F5F9" : "#1E293B",
    },

    noTicketsDiv: {
        display: "flex",
        margin: 40,
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
    },
}));

const reducer = (state, action) => {
    if (action.type === "LOAD_TICKETS") {
        const newTickets = action.payload;

        newTickets.forEach((ticket) => {
            const ticketIndex = state.findIndex((t) => t.id === ticket.id);
            if (ticketIndex !== -1) {
                state[ticketIndex] = ticket;
                if (ticket.unreadMessages > 0) {
                    state.unshift(state.splice(ticketIndex, 1)[0]);
                }
            } else {
                state.push(ticket);
            }
        });

        return [...state];
    }

    if (action.type === "LOAD_MOMENTS") {
        const newTickets = action.payload;

        newTickets.forEach((ticket) => {
            const ticketIndex = state.findIndex((t) => t.id === ticket.id);
            if (ticketIndex !== -1) {
                state[ticketIndex] = ticket;
                if (ticket.unreadMessages > 0) {
                    state.unshift(state.splice(ticketIndex, 1)[0]);
                }
            } else {
                state.push(ticket);
            }
        });

        return [...state];
    }

    if (action.type === "RESET_UNREAD") {
        const ticketId = action.payload;

        const ticketIndex = state.findIndex((t) => t.id === ticketId);
        if (ticketIndex !== -1) {
            state[ticketIndex] = { ...state[ticketIndex], unreadMessages: 0 };
        }

        return [...state];
    }

    if (action.type === "UPDATE_TICKET") {
        const ticket = action.payload;

        const ticketIndex = state.findIndex((t) => t.id === ticket.id);
        if (ticketIndex !== -1) {
            state[ticketIndex] = ticket;
        } else {
            state.unshift(ticket);
        }

        return [...state];
    }

    if (action.type === "UPDATE_TICKET_UNREAD_MESSAGES") {
        const ticket = action.payload;

        const ticketIndex = state.findIndex((t) => t.id === ticket.id);
        if (ticketIndex !== -1) {
            state[ticketIndex] = ticket;
            state.unshift(state.splice(ticketIndex, 1)[0]);
        } else {
            state.unshift(ticket);
        }

        return [...state];
    }


    if (action.type === "UPDATE_TICKET_CONTACT") {
        const contact = action.payload;
        const ticketIndex = state.findIndex((t) => t.contactId === contact.id);
        if (ticketIndex !== -1) {
            state[ticketIndex] = { ...state[ticketIndex], contact };
        }
        return [...state];
    }

    if (action.type === "UPDATE_TICKET_PRESENCE") {
        const data = action.payload;
        const ticketIndex = state.findIndex((t) => t.id === data.ticketId);
        if (ticketIndex !== -1) {
            state[ticketIndex] = { ...state[ticketIndex], presence: data.presence };
        }
        return [...state];
    }

    if (action.type === "DELETE_TICKET") {
        const ticketId = action.payload;
        const ticketIndex = state.findIndex((t) => t.id === ticketId);
        if (ticketIndex !== -1) {
            state.splice(ticketIndex, 1);
        }

        return [...state];
    }

    if (action.type === "RESET") {
        return [];
    }
};

const TicketsListCustom = (props) => {
    const {
        // handleChangeTab,
        status,
        searchParam,
        tags,
        users,
        showAll,
        selectedQueueIds,
        updateCount,
        style,
        whatsappIds,
        forceSearch,
        statusFilter,
        userFilter,
        user
    } = props;

    const classes = useStyles();
    const { ticketId } = useParams();
    const [pageNumber, setPageNumber] = useState(1);
    const [ticketsList, dispatch] = useReducer(reducer, []);
    
    const allTicket = user?.allTicket === 'enable';
    const allowGroup = user?.allowGroup;
    const profile = user?.profile;
    const queues = user?.queues || [];
    const usersIdsTicketPermission = user?.ticketPermissions?.map(permission => permission.targetUserId) || [];

    useEffect(() => {
        dispatch({ type: "RESET" });
        setPageNumber(1);
    }, [status, searchParam, dispatch, showAll, tags, users, forceSearch, selectedQueueIds, whatsappIds, statusFilter]);

    const { tickets, hasMore, loading } = useTickets({
        pageNumber,
        searchParam,
        status,
        showAll,
        tags: JSON.stringify(tags),
        users: JSON.stringify(users),
        queueIds: JSON.stringify(selectedQueueIds),
        whatsappIds: JSON.stringify(whatsappIds),
        statusFilter: JSON.stringify(statusFilter),
        userFilter
    });


    useEffect(() => {
        const queueIds = queues.map((q) => q.id);
        const filteredTickets = tickets.filter(
            (t) => (queueIds.indexOf(t.queueId) > -1) || (usersIdsTicketPermission?.includes(t.userId))
        );
        // const allticket = user.allTicket === 'enable';

        if (profile === "admin" || profile === "supervisor" || allTicket || allowGroup) {
            dispatch({ type: "LOAD_TICKETS", payload: tickets });
        } else {
            dispatch({ type: "LOAD_TICKETS", payload: filteredTickets });
        }

    }, [tickets]);

    useEffect(() => {
        if (!user) return;
        const companyId = user?.companyId;
        const socket = socketConnection({ companyId, userId: user.id });

        const shouldUpdateTicketAdmin = (ticket) =>
            (!ticket?.userId || ticket?.userId === user?.id || showAll) &&
            (!ticket?.queueId || selectedQueueIds.indexOf(ticket?.queueId) > -1) &&
            (!status || ticket?.status === status);

        const shouldUpdateTicketUser = (ticket) =>
            ((ticket?.status === "open" && usersIdsTicketPermission?.includes(ticket?.userId)) ||
            (selectedQueueIds.indexOf(ticket?.queueId) > -1 && (ticket?.userId === user?.id || !ticket?.userId))) &&
            (!status || ticket?.status === status);

        const notBelongsToUserQueues = (ticket) =>
            ticket.queueId && selectedQueueIds.indexOf(ticket.queueId) === -1;

        const userHasPermissionOnTicket = (ticket) =>
            usersIdsTicketPermission?.includes(ticket?.userId);

        socket.on("connect", () => {
            if (status) {
                socket.emit("joinTickets", status);
            } else {
                socket.emit("joinNotification");
            }
        });

        socket.on(`company-${companyId}-ticket`, (data) => {

    
            if (data.action === "updateUnread") {
                dispatch({
                    type: "RESET_UNREAD",
                    payload: data.ticketId,
                });
            }

            if (data.action === "update" && (user.profile === "admin" || user.profile === "supervisor" || allTicket) ? shouldUpdateTicketAdmin(data.ticket) : shouldUpdateTicketUser(data.ticket)) {

                dispatch({
                    type: "UPDATE_TICKET",
                    payload: data.ticket,
                    // isGroup: isGroup
                });
            }

            if (data.action === "update" && notBelongsToUserQueues(data.ticket) && !userHasPermissionOnTicket(data.ticket)) {
                dispatch({ type: "DELETE_TICKET", payload: data.ticket.id });
            }

            if (data.action === "delete") {
                dispatch({ type: "DELETE_TICKET", payload: data.ticketId });
            }
        });

        socket.on(`company-${companyId}-appMessage`, (data) => {
            const queueIds = queues.map((q) => q.id);
            if (
                profile === "user" &&
                (queueIds.indexOf(data.ticket.queue?.id) === -1 ||
                    data.ticket.queue === null)
            ) {
                return;
            }

            if (data.action === "create" && (user.profile === "admin" || user.profile === "supervisor" || allTicket) ? shouldUpdateTicketAdmin(data.ticket) : shouldUpdateTicketUser(data.ticket)) {
                dispatch({
                    type: "UPDATE_TICKET_UNREAD_MESSAGES",
                    payload: data.ticket,
                });
            }
        });

        socket.on(`company-${companyId}-contact`, (data) => {
            if (data.action === "update") {
                dispatch({
                    type: "UPDATE_TICKET_CONTACT",
                    payload: data.contact,
                });
            }
        });

        socket.on(`company-${companyId}-presence`, (data) => {

            dispatch({
                type: "UPDATE_TICKET_PRESENCE",
                payload: data,
            });
        });

        return () => {
            socket.disconnect();
        };
    }, [status, searchParam, showAll, user, selectedQueueIds, tags, users, profile, queues, whatsappIds, statusFilter, allTicket]);

    useEffect(() => {
        if (typeof updateCount === "function") {
            updateCount(ticketsList.length);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ticketsList]);

    if (!user) return null;

    const loadMore = () => {
        setPageNumber((prevState) => prevState + 1);
    };

    const handleScroll = (e) => {
        if (!hasMore || loading) return;

        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

        if (scrollHeight - (scrollTop + 100) < clientHeight) {
            loadMore();
        }
    };

    return (
        <Paper className={classes.ticketsListWrapper} style={style}>
            <Paper
                square
                name="closed"
                elevation={0}
                className={classes.ticketsList}
                onScroll={handleScroll}
            >
                <List style={{ paddingTop: 0 }} >
                    {ticketsList.length === 0 && !loading ? (
                        <div className={classes.noTicketsDiv}>
                            <span className={classes.noTicketsTitle}>
                                {i18n.t("ticketsList.noTicketsTitle")}
                            </span>
                            <p className={classes.noTicketsText}>
                                {i18n.t("ticketsList.noTicketsMessage")}
                            </p>
                        </div>
                    ) : (
                        <>
                            {ticketsList.map((ticket) => (
                                <TicketListItem
                                    ticket={ticket}
                                    user={user}
                                    key={ticket.id}
                                    selected={ticketId && ticketId === ticket.uuid}
                                />
                            ))}
                        </>
                    )}
                    {loading && <TicketsListSkeleton />}
                </List>
            </Paper>
        </Paper>
    );
};

export default TicketsListCustom;
